import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import usePerformanceSettings from '../../hooks/usePerformanceSettings';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import {
  useApiPagedList, useManagedPeriods, useTeamGoalActions,
  buildTeamGoalColumns, buildRevisionColumns, fetchTeamWeightTotals,
  PENDING_STEPS_URL, buildPendingStepColumns,
  isDeletableGoalStatus, resolveGoalDeepLinkMode, resolveRevisionDeepLinkMode, canDecidePendingGoalRow,
} from './config';
import { canDecideOnTeamGoals, canSeeHrQueues } from './teamGoalsGates';
import GoalDecisionModal from './GoalDecisionModal';
import RevisionDecisionModal from './RevisionDecisionModal';
import PeriodEndScoringModal from './PeriodEndScoringModal';
import GenericSelect from '../../components/ui/GenericSelect';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Tabs from '../../components/ui/Tabs';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { parseTableParams } from '../../utils/Utils';

// Task 27 — Ekip Hedefleri sayfası: hedef onayları (yönetici + koşullu İK
// kuyruğu), ekip dönem-içi takip, dönem sonu yönetici puanlaması ve (flag'e
// tabi) revizyon talepleri onay zinciri. Task 25/26'nın kurduğu sayfa
// desenlerini (hook/DataTable/i18n) birebir izler — dört sekmenin her biri
// kendi bağımsız DataTable sayfalama/filtre state'ine sahip (useApiPagedList,
// bkz. config/index.js).
const Container = styled.div``;

const PeriodSelectWrap = styled.div`
  min-width: 280px;
  margin-bottom: 16px;
`;

const SubTabsRow = styled.div`
  margin-bottom: 10px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
`;

const EmptyBox = styled.div`
  padding: 26px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// P3 — canDecideSteps: kilometre taşı adımlarına karar (yalnız karar yetkili satırda; skip-level hariç).
const emptyGoalModal = { open: false, goalId: null, isHr: false, readOnly: false, canDecideSteps: false };
const emptyScoringModal = { open: false, goalId: null };
const emptyRevisionModal = { open: false, revision: null, isHr: false };
// Gelen kutusu derin bağlantısının parametreleri (PerformanceInboxLinks.GoalDecision / GoalRevision).
const DEEP_LINK_PARAMS = ['goal', 'tab', 'revision'];

export default function TeamGoalsPage() {
  const { t } = useTranslation();
  const { canWrite, isHr, user } = useAuth();
  // Faz 3 / PM-01 (FE ayağı, SESSİZ ÖLÜM NOKTASI) — kurallar saf fonksiyonlarda
  // (teamGoalsGates.js, gerekçe + mutasyon sözleşmesi orada). Aksiyon kolonları
  // eskiden `canWrite('PM')` ile açılıyordu; backend kapısı ilişkiye
  // devredildikten sonra bu satır değişmeseydi düzeltme hiçbir amire ulaşmazdı.
  const writable = canDecideOnTeamGoals({
    canWritePm: canWrite('PM'),
    hasSubordinates: user?.hasSubordinates,
  });
  const hrQueuesVisible = canSeeHrQueues({ canWritePm: canWrite('PM'), isHr });
  const { settings, loading: settingsLoading } = usePerformanceSettings();
  const revisionsEnabled = !!settings?.goalRevisionEnabled;
  // İK kuyruğu yalnız enterpriseAdmin'e: backend İK kararlarını (hr-decision,
  // revisions hr-decision) artık enterpriseAdmin'le sınırlıyor — PM.Write'lı ama
  // EA olmayan kullanıcıya 403'lenecek butonlar hiç gösterilmez.
  const hrPendingVisible = !!settings?.hrGoalApprovalEnabled && hrQueuesVisible;
  const hrRevisionsVisible = hrQueuesVisible;

  const [activeTab, setActiveTab] = useState('pending');
  const [pendingScope, setPendingScope] = useState('manager'); // manager|hr
  const [revisionScope, setRevisionScope] = useState('team'); // team|all
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  const { periods, periodsFetching } = useManagedPeriods();
  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(periods[0].id);
  }, [periods, selectedPeriodId]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  // Plan 3, Task 8 — periodId → usesGoalWeights haritası. PeriodGoalResponse
  // (buildTeamGoalColumns'ın satırları) dönem bilgisini EMBED ETMİYOR — yalnız
  // çıplak PeriodId (bkz. task-8-report.md) — bu yüzden ağırlık kolonunun HANGİ
  // satırda anlamlı olduğunu bilmek için `periods` (useManagedPeriods, active+
  // evaluation — hedef yazma/onay akışının açık olduğu TEK iki durum, tıpkı
  // backend IsGoalWritingOpen gibi) üzerinden bir arama haritası kurulur. "Onay
  // Bekleyenler" sekmesi periodId[eq] FİLTRESİ KULLANMADIĞI için (birden çok
  // döneme yayılabilir) bu harita üç sekmenin (pending/team/periodEnd) HEPSİNDE
  // aynı şekilde kullanılır — selectedPeriod'a özel bir kısayol YOK.
  const periodWeightFlags = useMemo(
    () => Object.fromEntries(periods.map((p) => [p.id, !!p.usesGoalWeights])),
    [periods],
  );

  // Plan 3, Task 8 fix (inceleme R1) — çalışan-grup toplamının DÜRÜST kaynağı:
  // dönemin FİLTRESİZ takım hedefleri (bkz. fetchTeamWeightTotals yorumu,
  // config/index.js). SEKMEDEN BAĞIMSIZ, yalnız `selectedPeriodId` değiştiğinde
  // (ve bayraklı bir dönemse — parite: bayraksız dönemde bu ek çağrı HİÇ
  // yapılmaz) BİR kez çekilir; "Onay Bekleyenler"/"Dönem Sonu" hücreleri bunu
  // `row.periodId === teamWeightTotals.periodId` eşleşmesiyle kullanır (bkz.
  // buildTeamGoalColumns).
  const [teamWeightTotals, setTeamWeightTotals] = useState({ periodId: null, map: {}, complete: true });
  useEffect(() => {
    if (!selectedPeriodId || !periodWeightFlags[selectedPeriodId]) {
      setTeamWeightTotals({ periodId: selectedPeriodId, map: {}, complete: true });
      return undefined;
    }
    let cancelled = false;
    fetchTeamWeightTotals(selectedPeriodId).then((result) => {
      if (!cancelled) setTeamWeightTotals({ periodId: selectedPeriodId, ...result });
    });
    return () => { cancelled = true; };
  }, [selectedPeriodId, periodWeightFlags]);

  // pendingScope yalnız İK görünümü kapalıysa (veya kapatıldıysa) "manager"a düşer.
  useEffect(() => {
    if (pendingScope === 'hr' && !hrPendingVisible) setPendingScope('manager');
  }, [pendingScope, hrPendingVisible]);
  useEffect(() => {
    if (revisionScope === 'all' && !hrRevisionsVisible) setRevisionScope('team');
  }, [revisionScope, hrRevisionsVisible]);
  useEffect(() => {
    if (activeTab === 'revisions' && !revisionsEnabled) setActiveTab('pending');
  }, [activeTab, revisionsEnabled]);

  const {
    getById, managerDecide, hrDecide, submitManagerScores, submitTeamFinalKpi, decideRevisionManager, decideRevisionHr,
    decideMilestoneStep, submitTeamFinalThreshold, deleteGoal, getRevisionById,
  } = useTeamGoalActions();

  // ── Onay Bekleyenler ────────────────────────────────────────────────────
  const [pendingQueryParams, setPendingQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [pendingFilterList, setPendingFilterList] = useState([]);
  const pendingUrl = pendingScope === 'hr' ? '/v1/period-goals' : '/v1/period-goals/team';
  const pendingRequestParams = useMemo(() => ({
    ...pendingQueryParams,
    'status[in]': pendingScope === 'hr' ? 'hr_approval' : 'manager_approval',
  }), [pendingQueryParams, pendingScope]);
  const pendingList = useApiPagedList(pendingUrl, pendingRequestParams, activeTab === 'pending');

  // ── Ekip Hedefleri ──────────────────────────────────────────────────────
  const [teamQueryParams, setTeamQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [teamFilterList, setTeamFilterList] = useState([]);
  const teamRequestParams = useMemo(() => ({
    ...teamQueryParams,
    ...(selectedPeriodId ? { 'periodId[eq]': selectedPeriodId } : {}),
  }), [teamQueryParams, selectedPeriodId]);
  const teamList = useApiPagedList('/v1/period-goals/team', teamRequestParams, activeTab === 'team' && !!selectedPeriodId);

  // ── Dönem Sonu ──────────────────────────────────────────────────────────
  const [periodEndQueryParams, setPeriodEndQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [periodEndFilterList, setPeriodEndFilterList] = useState([]);
  const periodEndRequestParams = useMemo(() => ({
    ...periodEndQueryParams,
    'status[in]': 'evaluating',
    ...(selectedPeriodId ? { 'periodId[eq]': selectedPeriodId } : {}),
  }), [periodEndQueryParams, selectedPeriodId]);
  const periodEndList = useApiPagedList('/v1/period-goals/team', periodEndRequestParams, activeTab === 'periodEnd' && !!selectedPeriodId);

  // ── Revizyon Talepleri (flag: GoalRevisionEnabled) ─────────────────────
  const [revisionQueryParams, setRevisionQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [revisionFilterList, setRevisionFilterList] = useState([]);
  const revisionUrl = revisionScope === 'all' ? '/v1/period-goals/revisions' : '/v1/period-goals/revisions/team';
  const revisionRequestParams = useMemo(() => ({
    ...revisionQueryParams,
    'status[in]': revisionScope === 'all' ? 'hr_approval' : 'manager_approval',
  }), [revisionQueryParams, revisionScope]);
  const revisionsList = useApiPagedList(revisionUrl, revisionRequestParams, activeTab === 'revisions' && revisionsEnabled);

  // ── Adım Onayları (P3 — kilometre taşı adımları, kabul bekleyen) ─────────
  const [stepsQueryParams, setStepsQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [stepsFilterList, setStepsFilterList] = useState([]);
  const stepsRequestParams = useMemo(() => ({
    ...stepsQueryParams,
    ...(selectedPeriodId ? { 'periodId[eq]': selectedPeriodId } : {}),
  }), [stepsQueryParams, selectedPeriodId]);
  const stepsList = useApiPagedList(PENDING_STEPS_URL, stepsRequestParams, activeTab === 'steps' && writable);

  // ── Modaller ────────────────────────────────────────────────────────────
  const [goalModal, setGoalModal] = useState(emptyGoalModal);
  const [scoringModal, setScoringModal] = useState(emptyScoringModal);
  const [revisionModal, setRevisionModal] = useState(emptyRevisionModal);

  const openDecision = useCallback((row, isHr) => setGoalModal({ open: true, goalId: row.id, isHr, readOnly: false, canDecideSteps: false }), []);
  // P3 — detay görünümünde karar yetkili yönetici kilometre taşı adımlarını kabul/ret edebilir.
  const openDetail = useCallback((row) => setGoalModal({
    open: true, goalId: row.id, isHr: false, readOnly: true, canDecideSteps: writable && !row.isSkipLevel,
  }), [writable]);
  const openStepGoal = useCallback((row) => setGoalModal({
    open: true, goalId: row.goalId, isHr: false, readOnly: true, canDecideSteps: true,
  }), []);

  // ── Derin bağlantı (müşteri geri bildirimi 2026-10-28 — Performans gelen kutusu buraya
  // bağlanır, bkz. backend PerformanceInboxLinks.GoalDecision/GoalRevision): ?goal=<goalId>
  // hedefin karar/detay modalını, ?tab=revisions&revision=<id> Revizyon Talepleri sekmesini +
  // o talebin karar modalını açar. Kayıt sayfanın o anki verisinde aranmaz — tekil GET (hedef)
  // ya da bekleyen-liste uçları (revizyon; tekil uç yok) ile yüklenir. Modal kapanınca
  // parametreler history.replace ile temizlenir (useDeepLinkParams — geri tuşu modalı yeniden
  // açmaz, yenileme aynı kaydı tekrar açmaz; PipPage/PerformanceMePage deseni).
  const { values: deepLink, clear: clearDeepLink } = useDeepLinkParams(DEEP_LINK_PARAMS);

  // Aynı bağlantı ikinci kez işlenmez (sekme/ayar değişimi effect'i yeniden koşturur); parametre
  // temizlenince işaret de sıfırlanır — aynı hedefe sonradan gelen yeni bağlantı yine açılır.
  const handledGoalLinkRef = useRef(null);
  useEffect(() => {
    const linkGoalId = deepLink.goal;
    if (!linkGoalId) { handledGoalLinkRef.current = null; return; }
    if (handledGoalLinkRef.current === linkGoalId) return;
    handledGoalLinkRef.current = linkGoalId;
    getById(linkGoalId).then((g) => {
      // Karar kipi yalnız hedef o aşamada bekliyorsa ve sayfanın kendi kuralı o aşamanın
      // aksiyonunu gösteriyorsa (satır menüleriyle aynı: writable / hrQueuesVisible).
      const mode = resolveGoalDeepLinkMode(g, { canDecideAsManager: writable, canDecideAsHr: hrQueuesVisible });
      setGoalModal({ open: true, goalId: linkGoalId, ...mode, canDecideSteps: false });
    });
  }, [deepLink.goal, getById, writable, hrQueuesVisible]);

  const handledRevisionLinkRef = useRef(null);
  useEffect(() => {
    const { tab, revision: revisionId } = deepLink;
    if (tab !== 'revisions' || !revisionId) { handledRevisionLinkRef.current = null; return; }
    if (handledRevisionLinkRef.current === revisionId) return;
    // Revizyon sekmesi ayara (GoalRevisionEnabled) bağlı — ayar gelmeden sekmeye geçilirse
    // yukarıdaki "kapalıysa pending'e dön" effect'i geçişi hemen geri alırdı.
    if (settingsLoading) return;
    handledRevisionLinkRef.current = revisionId;
    if (!revisionsEnabled) { clearDeepLink(); return; }
    setActiveTab('revisions');
    // Tek revizyon GET'i (kapsam iki liste ucunun birleşimi) — eskiden iki listede pageSize 200
    // ile aranıyordu ve büyük İK kuyruğunda bağlantı modalı açamıyordu.
    getRevisionById(revisionId).then((revision) => {
      // Bulunamadı / karar verilmiş / aksiyonu görünmüyor — sekme açık kalır, modal açılmaz.
      const mode = resolveRevisionDeepLinkMode(revision, {
        canDecideAsManager: writable, canDecideAsHr: hrRevisionsVisible,
      });
      if (!mode) return;
      setRevisionScope(mode.scope);
      setRevisionModal({ open: true, revision, isHr: mode.isHr });
    });
  }, [deepLink, settingsLoading, revisionsEnabled, writable, hrRevisionsVisible, clearDeepLink, getRevisionById]);

  const refreshSteps = stepsList.refresh;
  const closeGoalModal = useCallback(() => {
    setGoalModal(emptyGoalModal);
    // Adım kararları modal içinde anında yazılır — kuyruk kapanışta tazelenir (sekme pasifse istek atılmaz).
    refreshSteps();
    if (deepLink.goal) clearDeepLink();
  }, [refreshSteps, deepLink.goal, clearDeepLink]);
  const handleDecided = useCallback(() => {
    closeGoalModal();
    pendingList.refresh();
    teamList.refresh();
  }, [closeGoalModal, pendingList, teamList]);

  const openScoring = useCallback((row) => setScoringModal({ open: true, goalId: row.id }), []);
  const closeScoring = useCallback(() => setScoringModal(emptyScoringModal), []);
  const handleScored = useCallback(() => {
    closeScoring();
    periodEndList.refresh();
  }, [closeScoring, periodEndList]);

  const openRevisionDecision = useCallback((row, isHr) => setRevisionModal({ open: true, revision: row, isHr }), []);
  const closeRevisionModal = useCallback(() => {
    setRevisionModal(emptyRevisionModal);
    if (deepLink.revision) clearDeepLink();
  }, [deepLink.revision, clearDeepLink]);

  const handleRevisionDecided = useCallback(() => {
    closeRevisionModal();
    revisionsList.refresh();
  }, [closeRevisionModal, revisionsList]);

  // Müşteri geri bildirimi (2026-10-28) — İK'nın askıda kalan / terk edilmiş hedefi silmesi.
  // Yalnız İK görünürlüğü olan kullanıcıya (hrQueuesVisible — sayfanın mevcut İK kuralı) ve
  // yalnız silinebilir durumdaki satırda (isDeletableGoalStatus — backend GoalLifecycleRules).
  // Kapı backend'de (HrActorRules.IsHrActorAsync); onay ConfirmDialog, toast interceptor'da.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const refreshPending = pendingList.refresh;
  const refreshTeam = teamList.refresh;
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    const ok = await deleteGoal(deleteTarget.id);
    setDeleteBusy(false);
    setDeleteTarget(null);
    if (ok) {
      refreshPending();
      refreshTeam();
    }
  }, [deleteTarget, deleteGoal, refreshPending, refreshTeam]);
  const deleteActionFor = useCallback((row) => (
    hrQueuesVisible && isDeletableGoalStatus(row.status)
      ? [{ type: 'delete', label: t('performance.common.delete'), onClick: () => setDeleteTarget(row) }]
      : []
  ), [hrQueuesVisible, t]);

  // ── Kolonlar ────────────────────────────────────────────────────────────
  // Plan 3, Task 8 — brief bu sekme için çalışan-grup toplam göstergesini
  // AÇIKÇA istiyor (bkz. periodWeightFlags yorumu); fix (inceleme R1) —
  // teamWeightTotals artık dürüst (filtresiz) kaynaktan (bkz. o state'in
  // yorumu, üstte).
  const pendingBaseColumns = useMemo(
    () => buildTeamGoalColumns(t, pendingList.data, periodWeightFlags, teamWeightTotals),
    [t, pendingList.data, periodWeightFlags, teamWeightTotals],
  );
  // Onay Akışları > Hedef (2026-10-28) — astı/PM.Write'ı olmayan akış onaycısının satırları da
  // karar eylemi taşır (canDecidePendingGoalRow), bu yüzden kolon yalnız hiçbir satıra eylem
  // düşmüyorsa düşer.
  const pendingHasFlowRows = pendingList.data.some((r) => r.inApprovalFlow);
  const pendingColumns = useMemo(() => {
    if (!writable && !hrQueuesVisible && !pendingHasFlowRows) return pendingBaseColumns;
    return [...pendingBaseColumns, {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = pendingList.data[tableMeta.rowIndex];
          if (!row) return null;
          // Faz J (Task 4) — DTO-driven: karar eylemi row.isSkipLevel'de HİÇ
          // render edilmez. BE bu satırın hiçbir zaman karar-bağlamı bir
          // sorguda isSkipLevel=true dönmeyeceğini garanti eder (task-3-
          // report.md §1: "Karar uçları skip-level REDDİ") — FE yine de
          // bayrağa koşulsuz uyar, hangi sekmenin bunu üretebileceğine dair
          // bir varsayım kurmaz.
          if (row.isSkipLevel) return null;
          const actions = [
            // Onay Akışları > Hedef — yönetici kuralı YA DA aktörün onaycısı olduğu akış adımı.
            ...(canDecidePendingGoalRow(row, { writable }) ? [{
              type: 'edit',
              label: t('performance.teamGoals.actions.decide'),
              onClick: () => openDecision(row, pendingScope === 'hr'),
            }] : []),
            // Müşteri geri bildirimi (2026-10-28) — İK: onayda bekleyen hedefi silme.
            ...deleteActionFor(row),
          ];
          if (actions.length === 0) return null;
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    }];
  }, [writable, hrQueuesVisible, pendingHasFlowRows, pendingBaseColumns, pendingList.data, t, openDecision, pendingScope, deleteActionFor]);

  // Plan 3, Task 8 — teamWeightTotals=null (grup tooltip'siz düz hücre): bu
  // sekmede tek bir dönem zaten seçili ve liste o dönemdeki TÜM (aktif)
  // hedefleri kapsıyor (brief bu göstergeyi yalnız Onay Bekleyenler/Dönem Sonu
  // için istiyor — bkz. task-8-report.md yerleşim kararı).
  const teamBaseColumns = useMemo(
    () => buildTeamGoalColumns(t, teamList.data, periodWeightFlags, null),
    [t, teamList.data, periodWeightFlags],
  );
  const teamColumns = useMemo(() => [...teamBaseColumns, {
    name: 'rowActions',
    label: ' ',
    options: {
      filter: false, sort: false, download: false, print: false,
      customBodyRender: (_, tableMeta) => {
        const row = teamList.data[tableMeta.rowIndex];
        if (!row) return null;
        const actions = [{
          type: 'details',
          label: t('performance.teamGoals.actions.viewDetail'),
          onClick: () => openDetail(row),
        },
        // Müşteri geri bildirimi (2026-10-28) — İK: askıda kalan / terk edilmiş hedefi silme.
        ...deleteActionFor(row)];
        return <OptionsMenu actions={actions} row={row} />;
      },
    },
  }], [teamBaseColumns, teamList.data, t, openDetail, deleteActionFor]);

  // Plan 3, Task 8 (bkz. pendingBaseColumns yorumu, fix inceleme R1 dahil).
  const periodEndBaseColumns = useMemo(
    () => buildTeamGoalColumns(t, periodEndList.data, periodWeightFlags, teamWeightTotals),
    [t, periodEndList.data, periodWeightFlags, teamWeightTotals],
  );
  const periodEndColumns = useMemo(() => {
    if (!writable) return periodEndBaseColumns;
    return [...periodEndBaseColumns, {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = periodEndList.data[tableMeta.rowIndex];
          if (!row) return null;
          // Faz J (Task 4) — bkz. pendingColumns'taki AYNI DTO-driven guard yorumu.
          if (row.isSkipLevel) return null;
          const actions = [{
            type: 'edit',
            label: t('performance.teamGoals.actions.score'),
            onClick: () => openScoring(row),
          }];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    }];
  }, [writable, periodEndBaseColumns, periodEndList.data, t, openScoring]);

  // P3 — Adım Onayları: satır aksiyonu hedefin detayını adım kararlarıyla açar.
  const stepsBaseColumns = useMemo(() => buildPendingStepColumns(t, stepsList.data), [t, stepsList.data]);
  const stepsColumns = useMemo(() => [...stepsBaseColumns, {
    name: 'rowActions',
    label: ' ',
    options: {
      filter: false, sort: false, download: false, print: false,
      customBodyRender: (_, tableMeta) => {
        const row = stepsList.data[tableMeta.rowIndex];
        if (!row) return null;
        const actions = [{
          type: 'edit',
          label: t('performance.teamGoals.actions.decide'),
          onClick: () => openStepGoal(row),
        }];
        return <OptionsMenu actions={actions} row={row} />;
      },
    },
  }], [stepsBaseColumns, stepsList.data, t, openStepGoal]);

  const revisionBaseColumns = useMemo(() => buildRevisionColumns(t), [t]);
  const revisionColumns = useMemo(() => {
    if (!writable) return revisionBaseColumns;
    return [...revisionBaseColumns, {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = revisionsList.data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [{
            type: 'edit',
            label: t('performance.teamGoals.actions.decide'),
            onClick: () => openRevisionDecision(row, revisionScope === 'all'),
          }];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    }];
  }, [writable, revisionBaseColumns, revisionsList.data, t, openRevisionDecision, revisionScope]);

  // ── DataTable değişim handler'ları (her sekme kendi state'ine yazar) ───
  const handlePendingChange = useCallback((_a, tableState) => {
    setPendingFilterList(tableState.filterList);
    setPendingQueryParams(parseTableParams(tableState, pendingBaseColumns));
  }, [pendingBaseColumns]);
  const handleTeamChange = useCallback((_a, tableState) => {
    setTeamFilterList(tableState.filterList);
    setTeamQueryParams(parseTableParams(tableState, teamBaseColumns));
  }, [teamBaseColumns]);
  const handlePeriodEndChange = useCallback((_a, tableState) => {
    setPeriodEndFilterList(tableState.filterList);
    setPeriodEndQueryParams(parseTableParams(tableState, periodEndBaseColumns));
  }, [periodEndBaseColumns]);
  const handleRevisionChange = useCallback((_a, tableState) => {
    setRevisionFilterList(tableState.filterList);
    setRevisionQueryParams(parseTableParams(tableState, revisionBaseColumns));
  }, [revisionBaseColumns]);
  const handleStepsChange = useCallback((_a, tableState) => {
    setStepsFilterList(tableState.filterList);
    setStepsQueryParams(parseTableParams(tableState, stepsBaseColumns));
  }, [stepsBaseColumns]);

  const tabs = useMemo(() => {
    const list = [
      { key: 'pending', label: t('performance.teamGoals.tabs.pending') },
      { key: 'team', label: t('performance.teamGoals.tabs.team') },
      { key: 'periodEnd', label: t('performance.teamGoals.tabs.periodEnd') },
    ];
    if (revisionsEnabled) list.push({ key: 'revisions', label: t('performance.teamGoals.tabs.revisions') });
    // P3 — kabul bekleyen kilometre taşı adımları (karar yetkili kullanıcıya).
    if (writable) list.push({ key: 'steps', label: t('performance.teamGoals.tabs.steps') });
    return list;
  }, [t, revisionsEnabled, writable]);

  // ── DataTable'lar — Hooks kuralları gereği KOŞULSUZ üretilir (her render'da
  // aynı sırada çağrılmalı); JSX'te yalnız SONUÇLARI koşullu gösterilir (bkz.
  // MyGoalsPage'deki "dataTable" deseni — burada dört sekme için tekrarlanır).
  const pendingDataTable = useMemo(() => (
    <DataTable
      title={t('performance.teamGoals.tabs.pending')}
      fetching={pendingList.fetching}
      columns={pendingColumns}
      data={pendingList.data}
      totalRowCount={pendingList.pagination.totalRowCount}
      pageIndex={pendingList.pagination.pageIndex}
      pageSize={pendingList.pagination.pageSize}
      filterList={pendingFilterList}
      onFilterConfirm={setPendingFilterList}
      onTableChanged={handlePendingChange}
    />
  ), [t, pendingList.fetching, pendingColumns, pendingList.data, pendingList.pagination,
    pendingFilterList, handlePendingChange]);

  const teamDataTable = useMemo(() => (
    <DataTable
      title={t('performance.teamGoals.tabs.team')}
      fetching={teamList.fetching}
      columns={teamColumns}
      data={teamList.data}
      totalRowCount={teamList.pagination.totalRowCount}
      pageIndex={teamList.pagination.pageIndex}
      pageSize={teamList.pagination.pageSize}
      filterList={teamFilterList}
      onFilterConfirm={setTeamFilterList}
      onTableChanged={handleTeamChange}
    />
  ), [t, teamList.fetching, teamColumns, teamList.data, teamList.pagination,
    teamFilterList, handleTeamChange]);

  const periodEndDataTable = useMemo(() => (
    <DataTable
      title={t('performance.teamGoals.tabs.periodEnd')}
      fetching={periodEndList.fetching}
      columns={periodEndColumns}
      data={periodEndList.data}
      totalRowCount={periodEndList.pagination.totalRowCount}
      pageIndex={periodEndList.pagination.pageIndex}
      pageSize={periodEndList.pagination.pageSize}
      filterList={periodEndFilterList}
      onFilterConfirm={setPeriodEndFilterList}
      onTableChanged={handlePeriodEndChange}
    />
  ), [t, periodEndList.fetching, periodEndColumns, periodEndList.data, periodEndList.pagination,
    periodEndFilterList, handlePeriodEndChange]);

  const revisionsDataTable = useMemo(() => (
    <DataTable
      title={t('performance.teamGoals.tabs.revisions')}
      fetching={revisionsList.fetching}
      columns={revisionColumns}
      data={revisionsList.data}
      totalRowCount={revisionsList.pagination.totalRowCount}
      pageIndex={revisionsList.pagination.pageIndex}
      pageSize={revisionsList.pagination.pageSize}
      filterList={revisionFilterList}
      onFilterConfirm={setRevisionFilterList}
      onTableChanged={handleRevisionChange}
    />
  ), [t, revisionsList.fetching, revisionColumns, revisionsList.data, revisionsList.pagination,
    revisionFilterList, handleRevisionChange]);

  const stepsDataTable = useMemo(() => (
    <DataTable
      title={t('performance.teamGoals.tabs.steps')}
      fetching={stepsList.fetching}
      columns={stepsColumns}
      data={stepsList.data}
      totalRowCount={stepsList.pagination.totalRowCount}
      pageIndex={stepsList.pagination.pageIndex}
      pageSize={stepsList.pagination.pageSize}
      filterList={stepsFilterList}
      onFilterConfirm={setStepsFilterList}
      onTableChanged={handleStepsChange}
    />
  ), [t, stepsList.fetching, stepsColumns, stepsList.data, stepsList.pagination, stepsFilterList, handleStepsChange]);

  return (
    <Container>
      <GoalDecisionModal
        open={goalModal.open}
        goalId={goalModal.goalId}
        isHr={goalModal.isHr}
        readOnly={goalModal.readOnly}
        onClose={closeGoalModal}
        onDecided={handleDecided}
        getById={getById}
        managerDecide={managerDecide}
        hrDecide={hrDecide}
        canDecideSteps={goalModal.canDecideSteps}
        decideMilestoneStep={decideMilestoneStep}
      />

      <PeriodEndScoringModal
        open={scoringModal.open}
        goalId={scoringModal.goalId}
        onClose={closeScoring}
        onScored={handleScored}
        getById={getById}
        submitManagerScores={submitManagerScores}
        submitTeamFinalKpi={submitTeamFinalKpi}
        decideMilestoneStep={decideMilestoneStep}
        submitTeamFinalThreshold={submitTeamFinalThreshold}

      />

      <RevisionDecisionModal
        open={revisionModal.open}
        revision={revisionModal.revision}
        isHr={revisionModal.isHr}
        onClose={closeRevisionModal}
        onDecided={handleRevisionDecided}
        decideRevisionManager={decideRevisionManager}
        decideRevisionHr={decideRevisionHr}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('performance.goal.deleteDialog.title')}
        message={t('performance.goal.deleteDialog.message', { name: deleteTarget?.title || '' })}
        confirmText={t('performance.common.delete')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteBusy}
      />

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'pending' && (
        <>
          {hrPendingVisible && (
            <SubTabsRow>
              <Tabs
                tabs={[
                  { key: 'manager', label: t('performance.teamGoals.tabs.pendingManager') },
                  { key: 'hr', label: t('performance.teamGoals.tabs.pendingHr') },
                ]}
                activeTab={pendingScope}
                onChange={setPendingScope}
              />
            </SubTabsRow>
          )}
          {pendingDataTable}
        </>
      )}

      {activeTab === 'team' && (
        <>
          <PeriodSelectWrap>
            <GenericSelect
              label={t('performance.goal.selectPeriod')}
              value={selectedPeriodId}
              onChange={setSelectedPeriodId}
              options={periodOptions}
              idKey="id"
              labelKey="name"
            />
          </PeriodSelectWrap>
          {!periodsFetching && periods.length === 0 && (
            <Card><EmptyBox>{t('performance.goal.noPeriods')}</EmptyBox></Card>
          )}
          {periods.length > 0 && !selectedPeriodId && (
            <Card><EmptyBox>{t('performance.goal.selectPeriodFirst')}</EmptyBox></Card>
          )}
          {selectedPeriodId && teamDataTable}
        </>
      )}

      {activeTab === 'periodEnd' && (
        <>
          <PeriodSelectWrap>
            <GenericSelect
              label={t('performance.goal.selectPeriod')}
              value={selectedPeriodId}
              onChange={setSelectedPeriodId}
              options={periodOptions}
              idKey="id"
              labelKey="name"
            />
          </PeriodSelectWrap>
          {!periodsFetching && periods.length === 0 && (
            <Card><EmptyBox>{t('performance.goal.noPeriods')}</EmptyBox></Card>
          )}
          {periods.length > 0 && !selectedPeriodId && (
            <Card><EmptyBox>{t('performance.goal.selectPeriodFirst')}</EmptyBox></Card>
          )}
          {selectedPeriodId && periodEndDataTable}
        </>
      )}

      {activeTab === 'steps' && writable && (
        <>
          <PeriodSelectWrap>
            <GenericSelect
              label={t('performance.goal.selectPeriod')}
              value={selectedPeriodId}
              onChange={setSelectedPeriodId}
              options={periodOptions}
              idKey="id"
              labelKey="name"
            />
          </PeriodSelectWrap>
          {stepsDataTable}
        </>
      )}

      {activeTab === 'revisions' && revisionsEnabled && (
        <>
          {hrRevisionsVisible && (
            <SubTabsRow>
              <Tabs
                tabs={[
                  { key: 'team', label: t('performance.teamGoals.tabs.revisionsTeam') },
                  { key: 'all', label: t('performance.teamGoals.tabs.revisionsHr') },
                ]}
                activeTab={revisionScope}
                onChange={setRevisionScope}
              />
            </SubTabsRow>
          )}
          {revisionsDataTable}
        </>
      )}
    </Container>
  );
}
